import { ArrowRight, ArrowLeft, Laptop, Monitor, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter, Button, SelectionCard } from '../../../components/ui';
import { useFormStore } from '../../../store';
import { CURRENT_DEVICE_TYPE_OPTIONS, CURRENT_DEVICE_AGE_OPTIONS, DEVICE_CONDITION_OPTIONS, PAIN_POINTS_OPTIONS, BRAND_OPTIONS } from '../../../constants';
import styles from './Steps.module.css';

export const CurrentSetupStep = () => {
  // 1. Hook into the store
  const { formData, updateCurrentSetup, nextStep, prevStep } = useFormStore();
  const { currentSetup } = formData;

  // 2. Local state for notifications/validation

  // Derived state for cleaner conditional rendering
  const hasDevice = currentSetup.currentDeviceType !== 'none';

  const handleDeviceTypeChange = (value: string) => {
    const isNone = value === 'none';
    
    updateCurrentSetup({ 
      currentDeviceType: value as any,
      hasCurrentDevice: !isNone,
      // Reset dependent fields if "none" is selected
      ...(isNone && {
        currentDeviceBrand: '',
        currentDeviceAge: '',
        currentDeviceCondition: 'no_device',
        painPoints: [],
      }),
    });
  };

  const handlePainPointToggle = (point: string) => {
    const current = currentSetup.painPoints || [];
    const updatedPoints = current.includes(point)
      ? current.filter((p) => p !== point)
      : [...current, point];
    
    updateCurrentSetup({ painPoints: updatedPoints });
  };

  const handleNext = () => {
    nextStep();
  };

  return (
    <>
      {/* Ensure Toast only renders when there is a message */}
      {/* {toast && <Toast message={toast} onClose={() => setToast('')} />} */}
      
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Your Current Setup</CardTitle>
          <CardDescription>
            Tell us about your existing device (if any). This helps us understand what you're upgrading from.
          </CardDescription>
        </CardHeader>
        
        <CardContent className={styles.contentGap}>
          {/* Current Device Type Selection */}
          <div className={styles.sectionTitle}>
            <h4>Do you currently have a computer?</h4>
          </div>
          <div className={styles.optionsGrid}>
            {CURRENT_DEVICE_TYPE_OPTIONS.map((option: any) => (
              <SelectionCard
                key={option.value}
                selected={currentSetup.currentDeviceType === option.value}
                onClick={() => handleDeviceTypeChange(option.value)}
                icon={
                  option.value === 'laptop' ? <Laptop size={24} /> :
                  option.value === 'desktop' ? <Monitor size={24} /> :
                  option.value === 'both' ? (
                    <span className={styles.dualIcon}>
                      <Laptop size={18} /><Monitor size={18} />
                    </span>
                  ) : <span className={styles.iconEmoji}>{option.icon}</span>
                }
                title={option.label}
              />
            ))}
          </div>

          {/* Conditional Fields: Only show if they have a device */}
          {hasDevice && (
            <div className={styles.fadeEntry}>
              <div className={styles.sectionTitle}>
                <h4>What brand is your current device?</h4>
              </div>
              <div className={styles.optionsRow}>
                {BRAND_OPTIONS.slice(0, -1).map((brand: string) => (
                  <button
                    key={brand}
                    type="button"
                    className={`${styles.chipButton} ${currentSetup.currentDeviceBrand === brand ? styles.chipActive : ''}`}
                    onClick={() => updateCurrentSetup({ currentDeviceBrand: brand })}
                  >
                    {brand}
                  </button>
                ))}
              </div>

              <div className={styles.sectionTitle}>
                <h4>How old is your current device?</h4>
              </div>
              <div className={styles.optionsRow}>
                {CURRENT_DEVICE_AGE_OPTIONS.map((option: any) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.chipButton} ${currentSetup.currentDeviceAge === option.value ? styles.chipActive : ''}`}
                    onClick={() => updateCurrentSetup({ currentDeviceAge: option.value })}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className={styles.sectionTitle}>
                <h4>Current device condition</h4>
              </div>
              <div className={styles.optionsGrid}>
                {DEVICE_CONDITION_OPTIONS.filter((o: any) => o.value !== 'no_device').map((option: any) => (
                  <SelectionCard
                    key={option.value}
                    selected={currentSetup.currentDeviceCondition === option.value}
                    onClick={() => updateCurrentSetup({ currentDeviceCondition: option.value })}
                    icon={<AlertCircle size={20} />}
                    title={option.label}
                    description={option.description}
                    size="compact"
                  />
                ))}
              </div>

              <div className={styles.sectionTitle}>
                <h4>What issues do you face?</h4>
                <p className={styles.sectionSubtitle}>Select all that apply</p>
              </div>
              <div className={styles.optionsRow}>
                {PAIN_POINTS_OPTIONS.map((point: string) => (
                  <button
                    key={point}
                    type="button"
                    className={`${styles.chipButton} ${currentSetup.painPoints?.includes(point) ? styles.chipActive : ''}`}
                    onClick={() => handlePainPointToggle(point)}
                  >
                    {point}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* First-time buyer message */}
          {!hasDevice && currentSetup.currentDeviceType === 'none' && (
            <div className={styles.infoBox}>
              <span className={styles.infoIcon}>🎉</span>
              <div>
                <strong>First time buyer!</strong>
                <p>Great! We'll help you find the perfect first computer based on your needs.</p>
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className={styles.footerSpaceBetween}>
          <Button variant="outline" onClick={prevStep}>
            <ArrowLeft size={18} /> Back
          </Button>
          <Button onClick={handleNext} size="lg">
            Continue <ArrowRight size={18} />
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};